import React, { useState } from 'react';
import { auth, db } from '@/config/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, Loader2, Database, Users, MapPin, Monitor, Image as ImageIcon } from 'lucide-react';

const Setup = () => {
  const [status, setStatus] = useState([]);
  const [running, setRunning] = useState(false);
  const [completed, setCompleted] = useState(false);

  const addStatus = (message, success = true) => {
    setStatus(prev => [...prev, { message, success, time: new Date().toLocaleTimeString() }]);
  };

  const runSetup = async () => {
    setRunning(true);
    setStatus([]);

    try {
      // 1. Create Settings
      addStatus('Creating global settings...');
      await setDoc(doc(db, 'settings', 'global'), {
        siteName: 'Digital Screens',
        logo: null,
        primaryColor: '#6366f1',
        secondaryColor: '#8b5cf6',
        accentColor: '#f59e0b',
        darkMode: true,
        fontFamily: 'Inter',
        defaultDuration: 10,
        defaultResolution: '1920x1080',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      addStatus('✓ Global settings created');

      // 2. Create Demo Locations
      addStatus('Creating demo locations...');
      const locations = [
        { id: 'loc-london-1', name: 'London Central', city: 'London', address: '123 Oxford Street', region: 'South East', status: 'active', contactEmail: 'central@foodchain.com', contactPhone: '+44 20 1234 5678' },
        { id: 'loc-london-2', name: 'London Bridge', city: 'London', address: '45 Borough High St', region: 'South East', status: 'active', contactEmail: 'bridge@foodchain.com', contactPhone: '+44 20 2345 6789' },
        { id: 'loc-manchester-1', name: 'Manchester Arndale', city: 'Manchester', address: 'Arndale Centre', region: 'North West', status: 'active', contactEmail: 'arndale@foodchain.com', contactPhone: '+44 161 123 4567' },
        { id: 'loc-birmingham-1', name: 'Birmingham Bullring', city: 'Birmingham', address: 'Bullring Shopping Centre', region: 'West Midlands', status: 'active', contactEmail: 'bullring@foodchain.com', contactPhone: '+44 121 234 5678' },
        { id: 'loc-leeds-1', name: 'Leeds Trinity', city: 'Leeds', address: 'Trinity Leeds', region: 'Yorkshire', status: 'maintenance', contactEmail: 'trinity@foodchain.com', contactPhone: '+44 113 345 6789' },
      ];

      for (const loc of locations) {
        await setDoc(doc(db, 'locations', loc.id), {
          ...loc,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      addStatus(`✓ Created ${locations.length} locations`);

      // 3. Create Demo Screens
      addStatus('Creating demo screens...');
      const screens = [
        { id: 'screen-1', name: 'Main Menu Board', locationId: 'loc-london-1', orientation: 'landscape', resolution: '1920x1080', status: 'online', playlistId: null },
        { id: 'screen-2', name: 'Drinks Display', locationId: 'loc-london-1', orientation: 'portrait', resolution: '1080x1920', status: 'online', playlistId: null },
        { id: 'screen-3', name: 'Counter Display', locationId: 'loc-london-1', orientation: 'landscape', resolution: '1920x1080', status: 'offline', playlistId: null },
        { id: 'screen-4', name: 'Window Display', locationId: 'loc-london-2', orientation: 'landscape', resolution: '3840x2160', status: 'online', playlistId: null },
        { id: 'screen-5', name: 'Menu Board 1', locationId: 'loc-manchester-1', orientation: 'landscape', resolution: '1920x1080', status: 'online', playlistId: null },
        { id: 'screen-6', name: 'Menu Board 2', locationId: 'loc-manchester-1', orientation: 'landscape', resolution: '1920x1080', status: 'online', playlistId: null },
        { id: 'screen-7', name: 'Entrance Display', locationId: 'loc-birmingham-1', orientation: 'portrait', resolution: '1080x1920', status: 'online', playlistId: null },
        { id: 'screen-8', name: 'Main Display', locationId: 'loc-leeds-1', orientation: 'landscape', resolution: '1920x1080', status: 'maintenance', playlistId: null },
      ];

      for (const screen of screens) {
        await setDoc(doc(db, 'screens', screen.id), {
          ...screen,
          lastPing: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      addStatus(`✓ Created ${screens.length} screens`);

      // 4. Create Demo Media
      addStatus('Creating sample media entries...');
      const mediaItems = [
        { id: 'media-1', name: 'Burger Special.jpg', url: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=1920&h=1080&fit=crop', type: 'image', size: 245000, mimeType: 'image/jpeg' },
        { id: 'media-2', name: 'Pizza Menu.jpg', url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1920&h=1080&fit=crop', type: 'image', size: 312000, mimeType: 'image/jpeg' },
        { id: 'media-3', name: 'Drinks Selection.jpg', url: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=1080&h=1920&fit=crop', type: 'image', size: 189000, mimeType: 'image/jpeg' },
        { id: 'media-4', name: 'Today Special.jpg', url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1920&h=1080&fit=crop', type: 'image', size: 278000, mimeType: 'image/jpeg' },
        { id: 'media-5', name: 'Desserts.jpg', url: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=1920&h=1080&fit=crop', type: 'image', size: 156000, mimeType: 'image/jpeg' },
      ];

      for (const media of mediaItems) {
        await setDoc(doc(db, 'media', media.id), {
          ...media,
          storagePath: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      addStatus(`✓ Created ${mediaItems.length} media items`);

      // 5. Create Demo Playlist
      addStatus('Creating demo playlist...');
      await setDoc(doc(db, 'playlists', 'playlist-1'), {
        id: 'playlist-1',
        name: 'Main Menu Rotation',
        description: 'Default menu playlist for all screens',
        mediaIds: ['media-1', 'media-2', 'media-4', 'media-5'],
        duration: 10,
        assignedScreens: ['screen-1', 'screen-4', 'screen-5'],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update screens with playlist
      await setDoc(doc(db, 'screens', 'screen-1'), { playlistId: 'playlist-1' }, { merge: true });
      await setDoc(doc(db, 'screens', 'screen-4'), { playlistId: 'playlist-1' }, { merge: true });
      await setDoc(doc(db, 'screens', 'screen-5'), { playlistId: 'playlist-1' }, { merge: true });
      addStatus('✓ Created demo playlist');

      // 6. Create HQ Admin User
      addStatus('Creating HQ Admin user...');
      try {
        const adminCredential = await createUserWithEmailAndPassword(auth, 'admin@digitalscreens.com', 'admin123');
        await setDoc(doc(db, 'users', adminCredential.user.uid), {
          id: adminCredential.user.uid,
          uid: adminCredential.user.uid,
          email: 'admin@digitalscreens.com',
          name: 'HQ Administrator',
          role: 'hq_admin',
          assignedLocations: [],
          createdAt: serverTimestamp(),
          isOnline: false
        });
        addStatus('✓ Created HQ Admin (admin@digitalscreens.com / admin123)');
      } catch (e) {
        if (e.code === 'auth/email-already-in-use') {
          addStatus('⚠ HQ Admin user already exists', true);
        } else {
          throw e;
        }
      }

      // 7. Create Store Manager User
      addStatus('Creating Store Manager user...');
      try {
        const managerCredential = await createUserWithEmailAndPassword(auth, 'manager@store1.com', 'manager123');
        await setDoc(doc(db, 'users', managerCredential.user.uid), {
          id: managerCredential.user.uid,
          uid: managerCredential.user.uid,
          email: 'manager@store1.com',
          name: 'Store Manager',
          role: 'store_manager',
          assignedLocations: ['loc-london-1', 'loc-london-2'],
          createdAt: serverTimestamp(),
          isOnline: false
        });
        addStatus('✓ Created Store Manager (manager@store1.com / manager123)');
      } catch (e) {
        if (e.code === 'auth/email-already-in-use') {
          addStatus('⚠ Store Manager user already exists', true);
        } else {
          throw e;
        }
      }

      addStatus('🎉 Setup completed successfully!');
      setCompleted(true);

    } catch (error) {
      addStatus(`❌ Error: ${error.message}`, false);
      console.error('Setup error:', error);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Card className="w-full max-w-2xl bg-slate-800/50 border-slate-700 backdrop-blur-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <Database className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">Database Setup</CardTitle>
          <CardDescription className="text-slate-400">
            Initialize your Digital Screens database with demo data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <h3 className="text-white font-medium mb-3">This will create:</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span>5 Demo Locations</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Monitor className="w-4 h-4 text-purple-400" />
                <span>8 Demo Screens</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <ImageIcon className="w-4 h-4 text-amber-400" />
                <span>5 Media Items</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Users className="w-4 h-4 text-green-400" />
                <span>2 User Accounts</span>
              </div>
            </div>
          </div>

          {status.length > 0 && (
            <div className="mb-6 max-h-64 overflow-y-auto bg-slate-900/50 rounded-lg p-4 space-y-2">
              {status.map((s, i) => (
                <div key={i} className={`text-sm flex items-start gap-2 ${s.success ? 'text-green-400' : 'text-red-400'}`}>
                  <span className="text-slate-500 text-xs">{s.time}</span>
                  <span>{s.message}</span>
                </div>
              ))}
            </div>
          )}

          {completed ? (
            <Alert className="bg-green-900/30 border-green-700">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <AlertDescription className="text-green-300">
                Setup complete! You can now <a href="/login" className="underline font-medium">login</a> with:
                <br />
                <strong>HQ Admin:</strong> admin@digitalscreens.com / admin123
                <br />
                <strong>Manager:</strong> manager@store1.com / manager123
              </AlertDescription>
            </Alert>
          ) : (
            <Button 
              onClick={runSetup} 
              disabled={running}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {running ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Running Setup...
                </>
              ) : (
                'Initialize Database'
              )}
            </Button>
          )}

          <p className="text-xs text-slate-500 text-center mt-4">
            ⚠️ This should only be run once during initial setup
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Setup;
